import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { formatAmountWithLocale } from '../helper/common.helper';
import './express-payment-modal.scss';

// Safe to call repeatedly — registers the pieces the fee-comparison chart needs
ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, ChartLegend);

const EXPRESS_RATE = 0.059; // 5.9%
const EXPRESS_MIN = 590; // min 590 SEK
const STANDARD_RATE = 0.014; // 1.4%

interface ExpressPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** How the modal was opened:
   *  - 'info'    → from the "Read more" link: purely informational, no action
   *               buttons (close via the ✕ only).
   *  - 'confirm' → from the Express Payment toggle (validation passed): shows
   *               Accept + Reject buttons. */
  mode?: 'info' | 'confirm';
  /** Called when the user rejects in 'confirm' mode — reverts the toggle. */
  onReject?: () => void;
  calculatedAmount?: number;
  currency?: string;
}

const ExpressPaymentModal: React.FC<ExpressPaymentModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  mode = 'confirm',
  onReject,
  calculatedAmount = 0,
  currency = 'SEK',
}) => {
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState<'details' | 'chart'>('details');
  useEffect(() => {
    if (isOpen) setActiveTab('details'); // always reopen on the details view
  }, [isOpen]);

  // ── Sliding pill-tab indicator ─────────────────────────────────────────────
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const [indicatorReady, setIndicatorReady] = useState(false);

  const measureIndicator = useCallback(() => {
    const container = tabsRef.current;
    if (!container) return;
    const activeBtn = container.querySelector<HTMLElement>('.epm-tab.active');
    if (activeBtn) setIndicator({ left: activeBtn.offsetLeft, width: activeBtn.offsetWidth });
  }, []);

  useLayoutEffect(() => {
    measureIndicator();
  }, [activeTab, isOpen, measureIndicator]);

  useEffect(() => {
    if (!isOpen) {
      setIndicatorReady(false);
      return;
    }
    measureIndicator();
    const raf = requestAnimationFrame(() => {
      measureIndicator();
      setIndicatorReady(true);
    });
    const timers = [80, 260, 500].map((ms) => setTimeout(measureIndicator, ms));
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [isOpen, measureIndicator]);

  useEffect(() => {
    window.addEventListener('resize', measureIndicator);
    return () => window.removeEventListener('resize', measureIndicator);
  }, [measureIndicator]);

  // ── Fee maths (mirrors the requirement text: 5.9% min 590 vs 1.4%) ─────────
  const amt = Number(calculatedAmount) || 0;
  const hasAmount = amt > 0;
  const expressFee = hasAmount ? Math.max(amt * EXPRESS_RATE, EXPRESS_MIN) : 0;
  const standardFee = hasAmount ? amt * STANDARD_RATE : 0;
  const expressNet = Math.max(amt - expressFee, 0);
  const extraCost = Math.max(expressFee - standardFee, 0);

  const fmt = (v: number) => formatAmountWithLocale(v, currency || 'SEK', i18n.language);

  const barData = {
    labels: [t('EPM_EXPRESS') || 'Express', t('EPM_STANDARD') || 'Standard'],
    datasets: [
      {
        label: t('EPM_FEE') || 'Fee',
        data: [expressFee, standardFee],
        backgroundColor: ['#7c3aed', '#d8ccff'],
        hoverBackgroundColor: ['#6d28d9', '#c4b5fd'],
        borderRadius: 12,
        maxBarThickness: 80,
      },
    ],
  };

  const barOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: (ctx: any) => ` ${fmt(Number(ctx.parsed?.y ?? ctx.raw ?? 0))}` },
      },
    },
    animation: { duration: 750, easing: 'easeOutQuart' },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#1a1330', font: { size: 13, weight: '700' } } },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(124,58,237,0.08)' },
        ticks: { color: '#6b6480', font: { size: 11 }, callback: (v: any) => `${v}` },
      },
    },
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleReject = () => {
    onReject?.();
    onClose();
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg" className="express-payment-modal" backdrop="static">
      <Modal.Header closeButton className="border-0 pb-2">
        <Modal.Title className="w-100 text-center">
          <h5 className="modal-title-text mb-0">{t('EXPRESS_PAYMENT_REQUIREMENTS')}</h5>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2 pb-4 px-4">
        {/* Details */}
        <div className="express-payment-content">
          <p className="info-text mb-3">{t('EXPRESS_PAYMENT_INFO_TEXT_1')}</p>
          <p className="info-text mb-3">{t('EXPRESS_PAYMENT_INFO_TEXT_2')}</p>
          <p className="requirement-text mb-2">{t('EXPRESS_PAYMENT_REQUIREMENTS_TEXT')}</p>
        </div>
      </Modal.Body>

      {/* Info mode ("Read more") shows no action buttons — the ✕ closes it.
          Confirm mode (toggle) shows Accept + Reject. */}
      {mode === 'confirm' && (
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 epm-confirm-footer">
          <Button
            variant="outline-secondary"
            onClick={handleReject}
            className="epm-reject-btn"
          >
            {t('REJECT')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            className="epm-accept-btn"
            style={{
              backgroundColor: '#6C5CE7',
              borderColor: '#6C5CE7',
              borderRadius: '24px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '600',
            }}
          >
            {t('ACCEPT')}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ExpressPaymentModal;
